import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import courseService from '../services/courseService';
import { useAuth } from '../contexts/AuthContext';
import VideoPlayer from './VideoPlayer';
import '../styles/LessonEditor.css';

const LessonEditor = () => {
  const { appId, courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  
  // Check if we're on the new lesson page by examining the URL path
  const isNewLessonPage = location.pathname.includes('/lessons/new');
  const isNewLesson = isNewLessonPage || lessonId === 'new';
  
  console.log('Current path:', location.pathname);
  console.log('Is new lesson page:', isNewLessonPage);
  console.log('Lesson ID from params:', lessonId);
  console.log('Is new lesson:', isNewLesson);
  console.log('Course ID:', courseId);
  console.log('App ID:', appId);
  
  const videoInputRef = useRef(null);
  
  const [course, setCourse] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    order_index: 1,
    video_url: '',
    thumbnail_url: '',
    is_published: false
  });
  
  const [loading, setLoading] = useState(!isNewLesson);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  
  // Check if user has app_editor role
  const isAppEditor = currentUser?.roles?.some(role => 
    role.name === 'app_editor' || role.name === 'admin' || role.name === 'superadmin'
  );
  
  // Debug the video file state
  useEffect(() => {
    console.log('videoFile state changed:', videoFile);
  }, [videoFile]);
  
  // Scroll to error message when it changes
  useEffect(() => {
    if (error) {
      // Scroll to the top of the page where the error message is displayed
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);
  
  // Debug the video input ref
  useEffect(() => {
    console.log('videoInputRef:', videoInputRef);
    if (videoInputRef.current) {
      console.log('videoInputRef.current:', videoInputRef.current);
      
      // Add a direct event listener to the input element
      const inputElement = videoInputRef.current;
      inputElement.addEventListener('change', (e) => {
        console.log('Direct change event on video input:', e);
        console.log('Files from direct event:', e.target.files);
      });
    }
  }, [videoInputRef.current]);
  
  useEffect(() => {
    fetchCourse();
    if (!isNewLesson) {
      fetchLesson();
    } else {
      // For new lessons, reset loading state
      setLoading(false);
      console.log('Initializing new lesson form');
    }
  }, [courseId, lessonId, isNewLesson]);
  
  const fetchCourse = async () => {
    try {
      // Make sure courseId is a valid number
      if (!courseId) {
        console.error('Course ID is missing');
        setError('Course ID is missing. Please go back and try again.');
        return;
      }
      
      const numericCourseId = parseInt(courseId, 10);
      if (isNaN(numericCourseId)) {
        console.error(`Invalid course ID: ${courseId}`);
        setError('Invalid course ID. Please go back and try again.');
        return;
      }
      
      // Make sure appId is a valid number
      const numericAppId = parseInt(appId, 10) || 1;
      
      // Get the auth token
      const token = localStorage.getItem('token_admin');
      // Get the current domain
      const domain = window.location.hostname;
      
      console.log(`🔧 LessonEditor: Fetching course ${numericCourseId} for app ${numericAppId}`);
      console.log("🔧 LessonEditor: Auth token present:", !!token);
      
      // Use direct fetch with the app-specific endpoint
      const response = await fetch(`/api/apps/${numericAppId}/courses/${numericCourseId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          'X-App-Domain': domain,
          'X-SaltMarsh-App-Context': 'admin'
        },
        credentials: 'include'
      });
      
      console.log("🔧 LessonEditor: Course response status:", response.status);
      
      if (response.status === 200) {
        const data = await response.json();
        console.log("🔧 LessonEditor: Course data received:", data);
        
        // Extract course data from possible response structures
        const courseData = data.data || data.course || data;
        setCourse(courseData);
        
        // If it's a new lesson, set the order_index to be the next in sequence
        if (isNewLesson) {
          // Fetch lessons for this course to determine the next order index
          const lessonsResponse = await fetch(`/api/apps/${numericAppId}/courses/${numericCourseId}/lessons`, {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': token ? `Bearer ${token}` : '',
              'X-App-Domain': domain,
              'X-SaltMarsh-App-Context': 'admin'
            },
            credentials: 'include'
          });
          
          if (lessonsResponse.status === 200) {
            const lessonsData = await lessonsResponse.json();
            console.log("🔧 LessonEditor: Lessons data received:", lessonsData);
            
            // Extract lessons array from possible response structures
            const lessons = Array.isArray(lessonsData) ? lessonsData : 
                           (lessonsData.data && Array.isArray(lessonsData.data)) ? lessonsData.data :
                           (lessonsData.lessons && Array.isArray(lessonsData.lessons)) ? lessonsData.lessons : [];
            
            const nextOrderIndex = lessons.length > 0 
              ? Math.max(...lessons.map(l => l.order_index || 0)) + 1 
              : 1;
            
            setFormData(prev => ({
              ...prev,
              order_index: nextOrderIndex
            }));
          }
        }
      } else {
        console.error("🔧 LessonEditor: Failed to fetch course, status:", response.status);
        setError(`Failed to load course. Server returned status ${response.status}.`);
      }
    } catch (err) {
      console.error('🔧 LessonEditor: Error fetching course:', err);
      setError('Failed to load course. Please try again later.');
    }
  };
  
  const fetchLesson = async () => {
    try {
      setLoading(true);
      
      // Make sure lessonId is a valid number
      if (!lessonId) {
        setError('Lesson ID is missing. Please go back and try again.');
        return;
      }
      
      const numericLessonId = parseInt(lessonId, 10);
      if (isNaN(numericLessonId)) {
        setError('Invalid lesson ID. Please go back and try again.');
        return;
      }
      
      const numericCourseId = parseInt(courseId, 10);
      if (isNaN(numericCourseId)) {
        setError('Invalid course ID. Please go back and try again.');
        return;
      }
      
      // Make sure appId is a valid number
      const numericAppId = parseInt(appId, 10) || 1;
      
      // Get the auth token
      const token = localStorage.getItem('token_admin');
      // Get the current domain
      const domain = window.location.hostname;
      
      console.log(`🔧 LessonEditor: Fetching lesson ${numericLessonId} for course ${numericCourseId} and app ${numericAppId}`);
      console.log("🔧 LessonEditor: Auth token present:", !!token);
      
      // Use the correct endpoint according to the API documentation
      console.log(`🔧 LessonEditor: Using endpoint: /api/lessons/${numericLessonId}`);
      const response = await fetch(`/api/lessons/${numericLessonId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
          'X-App-Domain': domain,
          'X-SaltMarsh-App-Context': 'admin'
        },
        credentials: 'include'
      });
      
      console.log("🔧 LessonEditor: Lesson response status:", response.status);
      
      if (response.status === 200) {
        const data = await response.json();
        console.log("🔧 LessonEditor: Lesson data received:", data);
        
        // Extract lesson data from possible response structures
        const lessonData = data.data || data.lesson || data;
        
        setFormData({
          title: lessonData.title || '',
          content: lessonData.content || '',
          order_index: lessonData.order_index || 1,
          video_url: lessonData.video_url || '',
          thumbnail_url: lessonData.thumbnail_url || '',
          is_published: lessonData.is_published || false
        });
        
        if (lessonData.thumbnail_url) {
          setThumbnailPreview(lessonData.thumbnail_url);
        }
        
        setError(null);
      } else {
        console.error("🔧 LessonEditor: Failed to fetch lesson, status:", response.status);
        setError(`Failed to load lesson. Server returned status ${response.status}.`);
      }
    } catch (err) {
      console.error('🔧 LessonEditor: Error fetching lesson:', err);
      setError('Failed to load lesson. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnailFile(file);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      // Check file type
      const validTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/x-ms-wmv', 'video/x-flv', 'video/x-matroska'];
      
      if (!validTypes.includes(file.type)) {
        setError(`Invalid file type. Supported types: MP4, WebM, MOV, AVI, WMV, FLV, MKV. You provided: ${file.type}`);
        if (videoInputRef.current) {
          videoInputRef.current.value = ''; // Clear the file input
        }
        return;
      }
      
      // Check file size (500MB limit)
      const maxSize = 500 * 1024 * 1024; // 500MB in bytes
      
      if (file.size > maxSize) {
        setError(`File too large. Maximum size is 500MB. Your file is ${Math.round(file.size / (1024 * 1024))}MB`);
        if (videoInputRef.current) {
          videoInputRef.current.value = ''; // Clear the file input
        }
        return;
      }
      
      // Additional validation - check if file is empty
      if (file.size === 0) {
        setError('The selected file is empty. Please select a valid video file.');
        if (videoInputRef.current) {
          videoInputRef.current.value = ''; // Clear the file input
        }
        return;
      }
      
      // Additional validation - check file name length
      if (file.name.length > 255) {
        setError('File name is too long. Please rename the file to be shorter than 255 characters.');
        if (videoInputRef.current) {
          videoInputRef.current.value = ''; // Clear the file input
        }
        return;
      }
      
      setError(null); // Clear any previous errors
      setVideoFile(file);
    }
  };
  
  const handleVideoUpload = async () => {
    if (!videoFile) {
      setError('Please select a video file first');
      return;
    }
    
    try {
      setVideoUploading(true);
      setVideoUploadProgress(0);
      setError(null); // Clear any previous errors
      
      // Set up progress tracking
      const updateProgress = (percent) => {
        setVideoUploadProgress(percent);
      };
      
      // If we're editing an existing lesson, we need to upload the video
      if (!isNewLesson) {
        try {
          // Validate the lesson ID
          const numericLessonId = parseInt(lessonId, 10);
          if (isNaN(numericLessonId)) {
            throw new Error(`Invalid lesson ID: ${lessonId}`);
          }
          
          // Validate the course ID
          const numericCourseId = parseInt(courseId, 10);
          if (isNaN(numericCourseId)) {
            throw new Error(`Invalid course ID: ${courseId}`);
          }
          
          // Make sure appId is a valid number
          const numericAppId = parseInt(appId, 10) || 1;
          
          // Get the auth token
          const token = localStorage.getItem('token_admin');
          // Get the current domain
          const domain = window.location.hostname;
          
          console.log(`🔧 LessonEditor: Uploading video for lesson ${numericLessonId} of course ${numericCourseId}`);
          
          // Create FormData object for file upload
          const formData = new FormData();
          formData.append('video', videoFile);
          
          // Use XMLHttpRequest to track upload progress
          const xhr = new XMLHttpRequest();
          
          // Set up progress tracking
          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const percentComplete = Math.round((event.loaded / event.total) * 100);
              updateProgress(percentComplete);
            }
          });
          
          // Define promise for XMLHttpRequest
          const uploadPromise = new Promise((resolve, reject) => {
            console.log(`🔧 LessonEditor: Using endpoint for video upload: /api/lessons/${numericLessonId}/upload-video`);
            xhr.open('POST', `/api/lessons/${numericLessonId}/upload-video`, true);
            
            // Set headers
            xhr.setRequestHeader('Authorization', token ? `Bearer ${token}` : '');
            xhr.setRequestHeader('X-App-Domain', domain);
            xhr.setRequestHeader('X-SaltMarsh-App-Context', 'admin');
            
            xhr.onload = function() {
              if (this.status >= 200 && this.status < 300) {
                try {
                  const response = JSON.parse(xhr.responseText);
                  resolve(response);
                } catch (e) {
                  reject(new Error('Invalid JSON response from server'));
                }
              } else {
                reject(new Error(`Server returned status ${this.status}`));
              }
            };
            
            xhr.onerror = function() {
              reject(new Error('Network error occurred during upload'));
            };
            
            // Send the FormData object containing the file
            xhr.send(formData);
          });
          
          // Wait for upload to complete
          const updatedLesson = await uploadPromise;
          
          // Update the form data with the new video URL
          setFormData(prev => ({
            ...prev,
            video_url: updatedLesson.video_url || updatedLesson.data?.video_url
          }));
          
          setVideoUploadProgress(100);
          
          // Reset the file input
          if (videoInputRef.current) {
            videoInputRef.current.value = '';
          }
          
          setVideoFile(null);
        } catch (uploadError) {
          // Handle specific error cases
          if (uploadError.message && uploadError.message.includes('too large')) {
            setError(`File size error: ${uploadError.message}`);
          } else if (uploadError.message && uploadError.message.includes('Invalid file type')) {
            setError(`File type error: ${uploadError.message}`);
          } else if (uploadError.response && uploadError.response.status === 413) {
            setError(`File too large: The server rejected the file because it exceeds the maximum allowed size (500MB).`);
          } else if (uploadError.response && uploadError.response.status === 401) {
            setError(`Authentication error: Please log in again.`);
          } else if (uploadError.response && uploadError.response.status === 403) {
            setError(`Permission denied: You don't have permission to upload videos.`);
          } else if (uploadError.response && uploadError.response.data && uploadError.response.data.error) {
            setError(`Server error: ${uploadError.response.data.error}`);
          } else {
            setError(`Error uploading video: ${uploadError.message || 'Unknown error'}`);
          }
          
          setVideoUploadProgress(0);
          throw uploadError; // Re-throw to be caught by the outer catch
        }
      } else {
        // For new lessons, we'll just keep the file and upload it when saving the lesson
        
        // This is just a simulation for the UI
        let progress = 0;
        const interval = setInterval(() => {
          progress += 10;
          setVideoUploadProgress(progress);
          if (progress >= 100) {
            clearInterval(interval);
          }
        }, 300);
      }
    } catch (err) {
      // Error is already set in the inner catch block
      setVideoUploadProgress(0);
    } finally {
      setVideoUploading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      // Handle thumbnail upload if there's a new file
      let thumbnailUrl = formData.thumbnail_url;
      if (thumbnailFile) {
        // In a real app, you would upload the thumbnail to a server
        // and get back a URL. For now, we'll just use the preview URL.
        thumbnailUrl = thumbnailPreview;
      }
      
      // Prepare lesson data
      const lessonData = {
        ...formData,
        thumbnail_url: thumbnailUrl
      };
      
      console.log('🔧 LessonEditor: Submitting lesson data:', lessonData);
      console.log('🔧 LessonEditor: App ID:', appId);
      console.log('🔧 LessonEditor: Course ID:', courseId);
      console.log('🔧 LessonEditor: Lesson ID:', lessonId);
      console.log('🔧 LessonEditor: Is New Lesson:', isNewLesson);
      console.log('🔧 LessonEditor: Current URL path:', location.pathname);
      
      // Get the auth token
      const token = localStorage.getItem('token_admin');
      // Get the current domain
      const domain = window.location.hostname;
      
      let savedLesson;
      try {
        // Make sure courseId is a valid number
        const numericCourseId = parseInt(courseId, 10);
        if (isNaN(numericCourseId)) {
          throw new Error(`Invalid course ID: ${courseId}`);
        }
        
        // Make sure appId is a valid number
        const numericAppId = parseInt(appId, 10) || 1;
        
        // Set up common request options
        const requestOptions = {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
            'X-App-Domain': domain,
            'X-SaltMarsh-App-Context': 'admin'
          },
          credentials: 'include'
        };
        
        // Force create mode if we're on the /lessons/new path
        if (isNewLesson || isNewLessonPage || location.pathname.includes('/lessons/new')) {
          console.log(`🔧 LessonEditor: Creating new lesson with endpoint: /api/courses/${numericCourseId}/lessons`);
          
          const response = await fetch(`/api/courses/${numericCourseId}/lessons`, {
            method: 'POST',
            ...requestOptions,
            body: JSON.stringify(lessonData)
          });
          
          if (response.status === 201 || response.status === 200) {
            savedLesson = await response.json();
            console.log('🔧 LessonEditor: Lesson created successfully:', savedLesson);
            
            // Extract lesson data from possible response structures
            const newLesson = savedLesson.data || savedLesson.lesson || savedLesson;
            
            // If we have a video file for a new lesson, upload it now
            if (videoFile && newLesson.id) {
              // Create FormData object for file upload
              const formData = new FormData();
              formData.append('video', videoFile);
              
              console.log(`🔧 LessonEditor: Uploading video with endpoint: /api/lessons/${newLesson.id}/upload-video`);
              const uploadResponse = await fetch(`/api/lessons/${newLesson.id}/upload-video`, {
                method: 'POST',
                headers: {
                  'Authorization': token ? `Bearer ${token}` : '',
                  'X-App-Domain': domain,
                  'X-SaltMarsh-App-Context': 'admin'
                },
                body: formData,
                credentials: 'include'
              });
              
              if (uploadResponse.status === 200) {
                const uploadResult = await uploadResponse.json();
                console.log('🔧 LessonEditor: Video uploaded successfully:', uploadResult);
              } else {
                console.error(`🔧 LessonEditor: Failed to upload video, status: ${uploadResponse.status}`);
              }
            }
          } else {
            const errorData = await response.json();
            throw new Error(`Failed to create lesson: ${errorData.message || response.statusText}`);
          }
        } else {
          // Make sure lessonId is a valid number
          if (!lessonId) {
            throw new Error('Lesson ID is missing');
          }
          
          const numericLessonId = parseInt(lessonId, 10);
          if (isNaN(numericLessonId)) {
            throw new Error(`Invalid lesson ID: ${lessonId}`);
          }
          
          console.log(`🔧 LessonEditor: Updating lesson with endpoint: /api/lessons/${numericLessonId}`);
          
          const response = await fetch(`/api/lessons/${numericLessonId}`, {
            method: 'PUT',
            ...requestOptions,
            body: JSON.stringify(lessonData)
          });
          
          if (response.status === 200) {
            savedLesson = await response.json();
            console.log('🔧 LessonEditor: Lesson updated successfully:', savedLesson);
          } else {
            const errorData = await response.json();
            throw new Error(`Failed to update lesson: ${errorData.message || response.statusText}`);
          }
        }
        
        // Navigate to the lesson management page
        navigate(`/apps/${appId}/courses/${courseId}/lessons`);
      } catch (apiError) {
        console.error('🔧 LessonEditor: API Error details:', {
          message: apiError.message,
          response: apiError.response ? {
            status: apiError.response.status,
            statusText: apiError.response.statusText,
            data: apiError.response.data
          } : 'No response data',
          request: apiError.request ? 'Request object exists' : 'No request object'
        });
        
        // Set a more detailed error message
        if (apiError.response && apiError.response.data && apiError.response.data.message) {
          setError(`Server error: ${apiError.response.data.message}`);
        } else if (apiError.response && apiError.response.status === 500) {
          setError('Server error (500): The server encountered an unexpected condition. Please check if all required fields are filled correctly.');
        } else {
          setError(`Failed to save lesson: ${apiError.message}`);
        }
      }
    } catch (err) {
      console.error('🔧 LessonEditor: Error in form submission:', err);
      setError('Failed to process form. Please try again later.');
    } finally {
      setSaving(false);
    }
  };
  
  if (!isAppEditor) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          You do not have permission to access this page. Only app editors can manage lessons.
        </div>
      </div>
    );
  }
  
  return (
    <div className="lesson-editor container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>{isNewLesson ? 'Create New Lesson' : 'Edit Lesson'}</h1>
          {course && (
            <h5 className="text-muted">Course: {course.title}</h5>
          )}
        </div>
      </div>
      
      {/* Display error messages prominently */}
      {error && (
        <div className="alert alert-danger mb-4">
          <div className="d-flex align-items-center">
            <i className="fas fa-exclamation-circle me-2" style={{ fontSize: '1.5rem' }}></i>
            <div>
              <strong>Error:</strong> {error}
            </div>
          </div>
        </div>
      )}
      
      {loading ? (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="title" className="form-label">Lesson Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  className="form-control"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="mb-3">
                <label htmlFor="content" className="form-label">Content *</label>
                <textarea
                  id="content"
                  name="content"
                  className="form-control"
                  rows="10"
                  value={formData.content}
                  onChange={handleInputChange}
                  required
                ></textarea>
                <small className="form-text text-muted">
                  You can use Markdown formatting in the content.
                </small>
              </div>
              
              <div className="mb-3">
                <label htmlFor="order_index" className="form-label">Order Index</label>
                <input
                  type="number"
                  id="order_index"
                  name="order_index"
                  className="form-control"
                  min="1"
                  value={formData.order_index}
                  onChange={handleInputChange}
                />
                <small className="form-text text-muted">
                  The order in which this lesson appears in the course. Lower numbers appear first.
                </small>
              </div>
              
              <div className="mb-3">
                <label htmlFor="video" className="form-label">Video</label>
                
                {/* Video Preview Section */}
                {formData.video_url && !videoUploading && !videoFile && (
                  <div className="video-preview-container mb-3">
                    <div className="card">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">Current Video</h6>
                        <button 
                          type="button" 
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => {
                            if (window.confirm('Are you sure you want to remove this video?')) {
                              setFormData(prev => ({ ...prev, video_url: '' }));
                            }
                          }}
                        >
                          <i className="fas fa-trash-alt me-1"></i> Remove
                        </button>
                      </div>
                      <div className="card-body p-0">
                        <VideoPlayer 
                          src={formData.video_url} 
                          poster={formData.thumbnail_url}
                          title={formData.title}
                          onError={(errorMessage) => {
                            setError(`Video playback error: ${errorMessage}`);
                          }}
                        />
                      </div>
                      <div className="card-footer bg-light">
                        <small className="text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          This is how the video will appear to users. Test the playback to ensure it works correctly.
                        </small>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Video Upload Section */}
                <div className={`video-upload-section ${formData.video_url && !videoFile ? 'mt-4' : ''}`}>
                  {!formData.video_url || videoFile ? (
                    <>
                      <div className="input-group">
                        <input
                          type="file"
                          id="video"
                          name="video"
                          className="form-control"
                          accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/x-ms-wmv,video/x-flv,video/x-matroska"
                          onChange={handleVideoChange}
                          ref={videoInputRef}
                          disabled={videoUploading}
                          key="video-input"
                        />
                        <button 
                          type="button" 
                          className="btn btn-outline-primary"
                          onClick={handleVideoUpload}
                          disabled={!videoFile || videoUploading}
                        >
                          <i className="fas fa-cloud-upload-alt me-1"></i> Upload
                        </button>
                      </div>
                      
                      <small className="form-text text-muted mt-1">
                        Supported formats: MP4, WebM, MOV, AVI, WMV, FLV, MKV. Maximum file size: 500MB.
                      </small>
                    </>
                  ) : (
                    <div className="d-grid gap-2 d-md-flex justify-content-md-start">
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => {
                          setVideoFile(null);
                          if (videoInputRef.current) {
                            videoInputRef.current.value = '';
                            videoInputRef.current.click();
                          }
                        }}
                      >
                        <i className="fas fa-exchange-alt me-1"></i> Replace Video
                      </button>
                    </div>
                  )}
                </div>
                
                {videoFile && !videoUploading && (
                  <div className="mt-3">
                    <div className="alert alert-info d-flex align-items-center">
                      <i className="fas fa-file-video me-3" style={{ fontSize: '1.5rem' }}></i>
                      <div>
                        <div><strong>Selected file:</strong> {videoFile.name}</div>
                        <div><strong>Size:</strong> {Math.round(videoFile.size / 1024 / 1024 * 10) / 10} MB</div>
                        <div><strong>Type:</strong> {videoFile.type}</div>
                      </div>
                    </div>
                  </div>
                )}
                
                {videoUploading && (
                  <div className="mt-3">
                    <div className="card">
                      <div className="card-body">
                        <h6 className="card-title">Uploading Video</h6>
                        <div className="progress mb-2">
                          <div 
                            className="progress-bar progress-bar-striped progress-bar-animated" 
                            role="progressbar" 
                            style={{ width: `${videoUploadProgress}%` }}
                            aria-valuenow={videoUploadProgress} 
                            aria-valuemin="0" 
                            aria-valuemax="100"
                          ></div>
                        </div>
                        <div className="d-flex justify-content-between">
                          <small className="text-muted">Uploading... {videoUploadProgress}%</small>
                          <small className="text-muted">Please don't close this page</small>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mb-3">
                <label htmlFor="thumbnail" className="form-label">Thumbnail Image</label>
                <input
                  type="file"
                  id="thumbnail"
                  name="thumbnail"
                  className="form-control"
                  accept="image/*"
                  onChange={handleThumbnailChange}
                />
                <small className="form-text text-muted">
                  Recommended size: 1280x720 pixels (16:9 ratio)
                </small>
                
                {thumbnailPreview && (
                  <div className="mt-2 thumbnail-preview-container">
                    <img 
                      src={thumbnailPreview} 
                      alt="Thumbnail preview" 
                      className="thumbnail-preview"
                    />
                  </div>
                )}
              </div>
              
              <div className="mb-4 form-check">
                <input
                  type="checkbox"
                  id="is_published"
                  name="is_published"
                  className="form-check-input"
                  checked={formData.is_published}
                  onChange={handleInputChange}
                />
                <label htmlFor="is_published" className="form-check-label">
                  Publish this lesson (make it visible to users)
                </label>
              </div>
              
              <div className="d-flex gap-2">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    isNewLesson ? 'Create Lesson' : 'Save Changes'
                  )}
                </button>
                
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => navigate(`/apps/${appId}/courses/${courseId}/lessons`)}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LessonEditor; 